import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trophy,
  CheckCircle,
  Brain,
  TrendingUp,
  Flame,
  AlertCircle,
  BarChart3,
  Target,
  Calendar,
  BadgeCheck,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  doc,
  getDoc,
  setDoc,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { useUserProgress } from "../../hooks/useGamification";
import { useLeaderboard } from "../../hooks/useGamification";
import { useVerificationStatus } from "../../hooks/useVerificationStatus";
import StreakCounter from "../components/StreakCounter";
import Leaderboard from "../components/Leaderboard";
import { safeToDate } from "../../utils/SafeToDate";
import SkillsValidationScheduler from "../components/SkillsValidationScheduler";

// Lightweight interactive line chart for HR metrics (Enhanced UI) - Adapted for Job Seeker
const PerformanceLineChart = ({ data }) => {
  const [hoverIndex, setHoverIndex] = useState(null);

  const width = 1000;
  const height = 400;
  const padding = 60;
  const graphHeight = height - padding * 2;
  const graphWidth = width - padding * 2;

  const metrics = [
    { key: "successRate", label: "Success Rate (%)", color: "#ef4444" }, // Red
    { key: "tests", label: "Tests Completed", color: "#2563eb" },       // Blue
    { key: "interviews", label: "Interviews", color: "#10b981" },    // Green
  ];

  const maxY = Math.max(
    10, // Minimum scale of 10
    ...data.flatMap((d) => [d.successRate || 0, d.tests || 0, d.interviews || 0])
  );

  const getCoordinates = (idx, value, metricOffset = 0) => {
    const xStep = data.length > 1 ? graphWidth / (data.length - 1) : 0;
    const x = padding + idx * xStep + metricOffset;
    const y = height - padding - (value / maxY) * graphHeight;
    return { x, y };
  };

  const pointsForMetric = (key, metricIndex) =>
    data.map((point, idx) => {
      // Offset each line slightly to prevent complete overlap when values are identical
      const offset = (metricIndex - 1) * 3;
      const { x, y } = getCoordinates(idx, point[key] || 0, offset);
      return { x, y, label: point.week, value: point[key] || 0 };
    });

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    // Calculate relative X coordinate scaled to SVG coordinate system
    const scaleX = width / rect.width;
    const mouseX = (e.clientX - rect.left) * scaleX;

    // Find nearest point
    const xStep = data.length > 1 ? graphWidth / (data.length - 1) : 0;
    const relativeX = mouseX - padding;
    const idx = Math.max(0, Math.min(data.length - 1, Math.round(relativeX / (xStep || 1))));

    if (idx !== hoverIndex) setHoverIndex(idx);
  };

  const hoverPoint = hoverIndex != null ? data[hoverIndex] : null;
  const xStep = data.length > 1 ? graphWidth / (data.length - 1) : 0;
  const hoverX = hoverIndex != null ? padding + hoverIndex * xStep : null;

  return (
    <div className="w-full">
      <div className="flex items-center gap-6 mb-6 justify-center sm:justify-start">
        {metrics.map((m) => (
          <div key={m.key} className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <span className="h-3 w-8 rounded-full" style={{ background: m.color }}></span>
            <span>{m.label}</span>
          </div>
        ))}
      </div>
      <div className="relative w-full aspect-[2.5/1]">
        {hoverPoint && hoverX != null && (
          <div
            className="pointer-events-none absolute bg-white/95 backdrop-blur-sm shadow-xl border border-gray-100 rounded-xl px-4 py-3 text-sm text-gray-800 transform -translate-x-1/2 transition-all duration-75"
            style={{
              left: `${(hoverX / width) * 100}%`,
              top: '0%',
              zIndex: 20
            }}
          >
            <div className="font-bold text-gray-900 border-b border-gray-100 pb-1 mb-1">{hoverPoint.week}</div>
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-3 text-red-600"><span>Success Rate:</span> <span className="font-bold">{hoverPoint.successRate}%</span></div>
              <div className="flex items-center justify-between gap-3 text-blue-600"><span>Tests:</span> <span className="font-bold">{hoverPoint.tests}</span></div>
              <div className="flex items-center justify-between gap-3 text-green-600"><span>Interviews:</span> <span className="font-bold">{hoverPoint.interviews}</span></div>
            </div>
          </div>
        )}
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full h-full"
          onMouseMove={handleMove}
          onMouseLeave={() => setHoverIndex(null)}
          preserveAspectRatio="xMidYMid meet"
        >

          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map((v) => {
            const y = height - padding - (v / 100) * graphHeight;
            return (
              <g key={v}>
                <line x1={padding} y1={y} x2={width - padding} y2={y} stroke="#f3f4f6" strokeWidth={1.5} strokeDasharray="4 4" />
              </g>
            );
          })}

          {/* Y Axis Labels */}
          <text x={padding - 15} y={height - padding} textAnchor="end" className="text-sm fill-gray-400 font-medium">0</text>
          <text x={padding - 15} y={height - padding - graphHeight / 2 + 5} textAnchor="end" className="text-sm fill-gray-400 font-medium">{Math.round(maxY / 2)}</text>
          <text x={padding - 15} y={padding + 5} textAnchor="end" className="text-sm fill-gray-400 font-medium">{Math.ceil(maxY)}</text>

          {/* X Axis labels */}
          {data.map((point, idx) => {
            const x = padding + idx * xStep;
            return (
              <text key={point.week} x={x} y={height - padding + 25} textAnchor="middle" className="text-sm fill-gray-500 font-medium">
                {point.week}
              </text>
            );
          })}

          {/* Lines */}
          {metrics.map((m, mIdx) => {
            const points = pointsForMetric(m.key, mIdx);
            // Line path
            const linePath = points
              .map((p, idx) => `${idx === 0 ? "M" : "L"}${p.x},${p.y}`)
              .join(" ");

            return (
              <g key={m.key}>
                {/* Stroke Line */}
                <path d={linePath} fill="none" stroke={m.color} strokeWidth={4} strokeLinejoin="round" strokeLinecap="round" className="drop-shadow-sm" />
                {/* Dots - Always visible now */}
                {points.map((p, idx) => (
                  <circle
                    key={idx}
                    cx={p.x}
                    cy={p.y}
                    r={5}
                    fill="white"
                    stroke={m.color}
                    strokeWidth={2.5}
                  />
                ))}
              </g>
            );
          })}

          {/* Hover guide line */}
          {hoverX != null && (
            <line x1={hoverX} y1={padding} x2={hoverX} y2={height - padding} stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="4 4" />
          )}
        </svg>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const db = getFirestore(firebaseApp);
  const [stats, setStats] = useState({
    testsCompleted: 0,
    interviewsDone: 0,
    successRate: 0,
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [graphData, setGraphData] = useState([]);
  const [rawData, setRawData] = useState([]); // Store all results for filtering
  const [graphFilter, setGraphFilter] = useState("weeks_7"); // daily, weeks_7, months_3, months_6, year_1

  const [profileCompletion, setProfileCompletion] = useState(0);
  const [profileCompletionItems, setProfileCompletionItems] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [validationGate, setValidationGate] = useState({
    loading: true,
    needsResume: false,
    needsTest: false,
    deadline: null,
    schedule: null,
    pendingTestId: null,
  });
  const [showFullLeaderboard, setShowFullLeaderboard] = useState(false);

  // Gamification Hooks
  const { progress: userProgress, loading: progressLoading } = useUserProgress(user?.id || "");
  const { leaderboard, loading: leaderboardLoading } = useLeaderboard("alltime", 5);
  const { isVerified } = useVerificationStatus();

  // Real-time Profile Data Listener
  useEffect(() => {
    if (!user) return;

    setLoadingStats(true); // Initial loading state

    const profileRef = doc(db, "userProfiles", user.id);
    const seekerRef = doc(db, "jobseekers", user.id);

    const unsubscribeProfile = onSnapshot(profileRef, async (profileSnap) => {
      try {
        // Fetch seeker data (less frequent changes, can stay valid for now or be separate listener if needed)
        // For simplicity, we'll fetch it once or listen to it if critical. 
        // Given we prioritize Profile updates, we can keep seeker static or assume merged data.
        const seekerSnap = await getDoc(seekerRef).catch(() => null);

        const profile = profileSnap.exists() ? profileSnap.data() : {};
        const seekerData = seekerSnap && seekerSnap.exists() ? seekerSnap.data() : {};

        // Merge data prioritizing userProfiles
        const mergedProfile = { ...seekerData, ...profile };
        setProfileData(mergedProfile);

        // Validation Logic
        const resumeUploaded = !!mergedProfile.resumeUploaded;
        const skillsVerified = !!mergedProfile.skillsVerified;
        const today = new Date();
        const isFifthOfMonth = today.getDate() === 5;
        const schedule = mergedProfile.skillsValidationSchedule || null;
        const pendingTestId = mergedProfile.pendingSkillsValidationTestId || null;

        const createdAtDate =
          safeToDate(mergedProfile.createdAt) ||
          new Date();

        let deadlineDate = safeToDate(mergedProfile.skillsValidationDeadline);

        // Initialize deadline if missing (auto-fix)
        if (!deadlineDate && profileSnap.exists()) {
          deadlineDate = new Date(createdAtDate.getTime() + 2 * 24 * 60 * 60 * 1000);
          // Non-blocking update
          setDoc(profileRef, { skillsValidationDeadline: Timestamp.fromDate(deadlineDate) }, { merge: true }).catch(console.error);
        }

        setValidationGate({
          loading: false,
          needsResume: !resumeUploaded || (resumeUploaded && isFifthOfMonth),
          needsTest: resumeUploaded && (!skillsVerified || (skillsVerified && isFifthOfMonth)),
          deadline: deadlineDate,
          schedule,
          pendingTestId,
        });

      } catch (error) {
        console.error("Error in profile listener:", error);
        setValidationGate(prev => ({ ...prev, loading: false }));
      }
    });

    return () => unsubscribeProfile();
  }, [user, db]);

  // Calculate profile completion
  useEffect(() => {
    const calculateProfileCompletion = async () => {
      if (!user || !profileData) return;

      try {
        const profile = profileData;
        const resumeData = profile.resumeData || {};

        const completionItems = [
          { label: "Name", completed: !!(profile?.name || resumeData?.personalInfo?.name || resumeData?.name), weight: 15 },
          { label: "Email", completed: !!(resumeData?.personalInfo?.email || resumeData?.email), weight: 15 },
          {
            label: "Phone",
            completed: !!(resumeData?.personalInfo?.phone && resumeData.personalInfo.phone !== "+1 (555) 123-4567"),
            weight: 10,
          },
          {
            label: "Location",
            completed: !!(profile?.location || resumeData?.personalInfo?.location) && profile?.location !== "Location not set" && resumeData?.personalInfo?.location !== "Location not provided",
            weight: 10,
          },
          { label: "Resume Uploaded", completed: !!profile.resumeUploaded, weight: 20 },
          { label: "Skills Added", completed: !!(resumeData?.professionalInfo?.skills?.length > 0 || resumeData?.skills?.length > 0), weight: 15 },
          { label: "Skills Verified", completed: !!isVerified, weight: 15 },
        ];

        const completedWeight = completionItems.reduce((sum, item) => sum + (item.completed ? item.weight : 0), 0);
        const totalWeight = completionItems.reduce((sum, item) => sum + item.weight, 0);
        const completionPercentage = Math.round((completedWeight / totalWeight) * 100);

        setProfileCompletion(completionPercentage);
        setProfileCompletionItems(completionItems);
      } catch (error) {
        console.error("Error calculating profile completion:", error);
      }
    };

    calculateProfileCompletion();
  }, [user, isVerified, profileData]);

  // Sync User Identity with Gamification Service (Fix for "User" in Leaderboard)
  // Sync User Identity with Leaderboard (Direct Write)
  useEffect(() => {
    if (!user || !profileData) return;

    const syncIdentity = async () => {
      try {
        const displayName =
          profileData.name ||
          profileData.resumeData?.personalInfo?.name ||
          user.displayName ||
          user.email?.split('@')[0];

        if (!displayName) return;

        // Direct write to ensure persistence
        const userProgressRef = doc(db, 'userProgress', user.id);

        // We use setDoc with merge to create the doc if it doesn't exist (edge case)
        await setDoc(userProgressRef, {
          displayName: displayName,
          name: displayName, // Redundant but safe
          email: user.email,
          updatedAt: serverTimestamp()
        }, { merge: true });

        // Also ensure an avatar is set if available
        if (profileData.avatar || user.photoURL) {
          await setDoc(userProgressRef, {
            avatar: profileData.avatar || user.photoURL
          }, { merge: true });
        }

      } catch (err) {
        console.error("Error syncing identity to leaderboard:", err);
      }
    };

    syncIdentity();
  }, [user, profileData, db]);

  // Fetch stats and generate graph data
  useEffect(() => {
    if (!user || validationGate.needsTest || validationGate.needsResume) return;

    const q = query(
      collection(db, "jobseekerResults"),
      where("jobSeekerId", "==", user.id),
      orderBy("completedAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const results = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          completedAt: safeToDate(data.completedAt) || new Date(),
          score: data.totalScore || data.score || data.interviewScore || 0,
          interviewScore: data.interviewScore,
        };
      });

      // Calculate current stats
      let totalScore = 0;
      let validScores = 0;
      let interviewsCount = 0;

      results.forEach((test) => {
        const testScore = test.score;
        if (testScore !== undefined && testScore !== null) {
          totalScore += testScore;
          validScores++;
        }
        if (test.interviewScore !== undefined && test.interviewScore !== null) {
          interviewsCount++;
        }
      });

      const avgSuccessRate = validScores > 0 ? Math.round(totalScore / validScores) : 0;

      setStats({
        testsCompleted: results.length,
        interviewsDone: interviewsCount,
        successRate: avgSuccessRate,
      });

      // Store raw results for graph filtering
      setRawData(results);
      setLoadingStats(false);
    });

    return () => unsubscribe();
  }, [user, db, validationGate.needsResume, validationGate.needsTest]);

  // Effect to generate graph data based on filter
  useEffect(() => {
    if (!rawData.length) return;

    const now = new Date();
    let points = [];

    const getInRange = (list, start, end) =>
      list.filter(item => item.completedAt >= start && item.completedAt < end);

    const calculateMetrics = (items) => {
      const count = items.length;
      const interviews = items.filter(r => r.interviewScore !== undefined && r.interviewScore !== null).length;
      const scores = items.map(r => r.score).filter(s => s > 0);
      const rate = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
      return { tests: count, interviews, successRate: rate };
    };

    if (graphFilter === 'daily') {
      // Last 7 days
      for (let i = 6; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        d.setHours(0, 0, 0, 0);

        const nextD = new Date(d);
        nextD.setDate(d.getDate() + 1);

        const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
        const items = getInRange(rawData, d, nextD);
        points.push({ ...calculateMetrics(items), week: dayName });
      }
    } else if (graphFilter === 'weeks_7') {
      // Last 7 weeks
      for (let i = 6; i >= 0; i--) {
        const start = new Date(now);
        start.setDate(start.getDate() - (i * 7 + (now.getDay() || 7) - 1));

        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - (i * 7));
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 7);

        const items = getInRange(rawData, weekStart, weekEnd);
        points.push({ ...calculateMetrics(items), week: `Week ${7 - i}` });
      }
    } else if (graphFilter === 'months_3') {
      // Last 12 weeks
      for (let i = 11; i >= 0; i--) {
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - (i * 7));
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 7);

        const items = getInRange(rawData, weekStart, weekEnd);
        points.push({ ...calculateMetrics(items), week: `W${12 - i}` });
      }
    } else if (graphFilter === 'months_6') {
      // Last 6 months
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);

        const monthName = d.toLocaleDateString('en-US', { month: 'short' });
        const items = getInRange(rawData, d, nextMonth);
        points.push({ ...calculateMetrics(items), week: monthName });
      }
    } else if (graphFilter === 'year_1') {
      // Last 12 months
      for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);

        const monthName = d.toLocaleDateString('en-US', { month: 'short' });
        const items = getInRange(rawData, d, nextMonth);
        points.push({ ...calculateMetrics(items), week: monthName });
      }
    }

    // Default fill if empty for weeks_7
    if (graphFilter === 'weeks_7' && points.length < 7) {
      while (points.length < 7) {
        points.unshift({ week: `Week ${points.length + 1}`, tests: 0, interviews: 0, successRate: 0 });
      }
    }

    setGraphData(points);
  }, [rawData, graphFilter]);

  if (validationGate.loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (validationGate.needsResume) {
    return (
      <div className="max-w-3xl mx-auto py-10">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Upload your resume to continue</h1>
          <p className="text-gray-700 mb-6">
            Finish your resume upload first. We will generate a personalized skills validation test right after.
          </p>
          <button
            onClick={() => navigate("/job-seeker/resume-upload")}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Upload Resume
          </button>
        </div>
      </div>
    );
  }

  if (validationGate.needsTest) {
    return (
      <SkillsValidationScheduler
        user={user}
        profileData={profileData}
        deadline={validationGate.deadline}
        schedule={validationGate.schedule}
        pendingTestId={validationGate.pendingTestId}
        onTestLaunch={(testId) =>
          navigate(`/job-seeker/take-test/${testId}`, {
            state: {
              message: "Complete your skills validation to unlock the dashboard.",
            },
            replace: true,
          })
        }
        onScheduleUpdate={({ schedule, pendingTestId }) =>
          setValidationGate((prev) => ({
            ...prev,
            schedule: schedule || prev.schedule,
            pendingTestId: pendingTestId || prev.pendingTestId,
          }))
        }
      />
    );
  }

  if (loadingStats || progressLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Modern Header Card with Starfield Animation */}
      <div className="relative overflow-hidden rounded-2xl shadow-lg bg-gradient-to-br from-red-900 via-red-800 to-red-700">
        {/* Starfield Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="stars-layer absolute inset-0 animate-stars-slow"></div>
          <div className="stars-layer absolute inset-0 animate-stars-medium" style={{ animationDelay: '-5s' }}></div>
          <div className="stars-layer absolute inset-0 animate-stars-fast" style={{ animationDelay: '-10s' }}></div>
        </div>

        <div className="relative px-6 sm:px-8 py-5 sm:py-6">
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-xl sm:text-2xl font-bold text-white">
              Welcome Back, {profileData?.name || user?.displayName || user?.email?.split('@')[0] || 'User'}
            </h1>
            {isVerified && !validationGate.needsTest && (
              <BadgeCheck className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400 flex-shrink-0" strokeWidth={2.5} />
            )}
          </div>
          <p className="text-red-100 text-sm sm:text-sm">Track your progress and stay ahead of the curve</p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Main Stats */}
        <div className="lg:col-span-2 space-y-6">
          {/* Performance Trends - Moved to Top */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                <BarChart3 className="w-5 h-5 text-red-600" />
                <span>Performance Trends</span>
              </h2>
              <div className="relative">
                <select
                  value={graphFilter}
                  onChange={(e) => setGraphFilter(e.target.value)}
                  className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block p-2 outline-none cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <option value="daily">Daily</option>
                  <option value="weeks_7">Last 7 Weeks</option>
                  <option value="months_3">Last 3 Months</option>
                  <option value="months_6">Last 6 Months</option>
                  <option value="year_1">Last Year</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-center py-4">
              <PerformanceLineChart data={graphData} />
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Tests Completed</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats.testsCompleted}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Interviews Done</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats.interviewsDone}</p>
                </div>
                <Brain className="w-10 h-10 text-blue-600" />
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Success Rate</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats.successRate}%</p>
                </div>
                <Trophy className="w-10 h-10 text-red-600" />
              </div>
            </div>
          </div>

          {/* Streak Card - Made Smaller */}
          {!progressLoading && userProgress && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                  <Flame className="w-4 h-4 text-orange-500" />
                  <span>Your Streak</span>
                </h2>
              </div>
              <StreakCounter
                currentStreak={userProgress.currentStreak || 0}
                longestStreak={userProgress.longestStreak || 0}
                lastActivityDate={userProgress.lastActivityDate}
                freezeTokens={userProgress.streakFreezeTokens || 0}
                compact={true}
              />
            </div>
          )}
        </div>

        {/* Right Column - Leaderboard and Profile Completion */}
        <div className="space-y-6">
          {/* Top 5 Leaderboard */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                <Trophy className="w-5 h-5 text-yellow-600" />
                <span>Top 5</span>
              </h2>
              <button
                onClick={() => setShowFullLeaderboard(true)}
                className="text-sm font-semibold text-red-600 hover:text-red-700"
              >
                Leaderboard
              </button>
            </div>
            {leaderboardLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
              </div>
            ) : leaderboard.length > 0 ? (
              <div className="space-y-2">
                {leaderboard.slice(0, 5).map((entry, index) => {
                  const rank = index + 1;
                  const isCurrentUser = user && entry.userId === user.id;
                  // Use local profile name for current user (immediate fix)
                  const displayName = isCurrentUser && profileData?.name
                    ? profileData.name
                    : (entry.userName || "Anonymous");

                  return (
                    <div
                      key={entry.userId}
                      className={`flex items-center justify-between p-3 rounded-lg ${isCurrentUser
                        ? "bg-red-50 border-2 border-red-500"
                        : rank === 1
                          ? "bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-300"
                          : rank === 2
                            ? "bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-300"
                            : rank === 3
                              ? "bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-300"
                              : "bg-gray-50 border border-gray-200"
                        }`}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-lg font-bold text-gray-700 w-6">{rank}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="font-semibold text-gray-900 text-sm truncate">
                              {displayName}
                              {isCurrentUser && (
                                <span className="ml-2 text-xs bg-red-600 text-white px-1.5 py-0.5 rounded">You</span>
                              )}
                            </p>
                            <span className="flex items-center space-x-1 text-xs text-orange-600">
                              <Flame className="w-3.5 h-3.5" />
                              <span className="font-medium">{entry.currentStreak || 0}</span>
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">{entry.levelName || "Explorer"}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">{(entry.points || 0).toLocaleString()}</p>
                        <p className="text-xs text-gray-500">pts</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No rankings yet</p>
              </div>
            )}
          </div>

          {showFullLeaderboard && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-6">
              <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col relative overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-red-50 via-white to-white">
                  <div className="flex items-center space-x-2">
                    <Trophy className="w-5 h-5 text-yellow-600" />
                    <h3 className="text-xl font-semibold text-gray-900">Leaderboard</h3>
                  </div>
                  <button
                    onClick={() => setShowFullLeaderboard(false)}
                    className="text-gray-500 hover:text-gray-900"
                    aria-label="Close leaderboard"
                  >
                    ✕
                  </button>
                </div>
                <div className="p-6 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                  <Leaderboard userId={user?.id} limitCount={50} currentUserName={profileData?.name} />
                </div>
              </div>
            </div>
          )}

          {/* Daily Activity - Coming Soon */}
          <div className="relative group bg-gradient-to-br from-red-50 via-red-50 to-red-100 rounded-xl shadow-sm border border-red-900 p-5 hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer">
            {/* Animated gradient border */}
            <div className="absolute inset-0 bg-gradient-to-r from-red-700 via-red-800 to-red-700 rounded-xl opacity-0 group-hover:opacity-20 blur transition-opacity duration-300 pointer-events-none"></div>

            {/* Animated glow effect */}
            <div className="absolute -inset-px bg-gradient-to-r from-red-700 via-red-800 to-red-700 rounded-xl opacity-0 group-hover:opacity-15 blur-lg animate-pulse transition-opacity duration-300 pointer-events-none"></div>

            <div className="relative z-10 flex items-center justify-between">
              {/* Left Section */}
              <div className="flex items-center space-x-4 flex-1">
                <div className="w-12 h-12 bg-gradient-to-br from-red-700 to-red-900 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-gray-900 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-red-800 group-hover:to-red-900 transition-all duration-300">
                    Daily Activity ⚡
                  </h3>
                  <p className="text-xs text-gray-700 font-medium mt-0.5">Wait for the challenges</p>
                </div>
              </div>

              {/* Right Section - Coming Soon with pulse */}
              <div className="text-right flex-shrink-0">
                <div className="relative inline-block">
                  <div className="absolute inset-0 bg-gradient-to-r from-red-700 to-red-800 rounded-full opacity-30 blur animate-pulse"></div>
                  <span className="relative px-3 py-1.5 bg-gradient-to-r from-red-700 to-red-800 text-white text-xs font-bold rounded-full shadow-md group-hover:shadow-lg transition-shadow duration-300">
                    🚀 Coming soon
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Career Passport - Coming Soon - Enhanced */}
          <div className="relative group bg-gradient-to-br from-red-50 via-red-50 to-red-100 rounded-xl shadow-sm border border-red-900 p-5 hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer">
            {/* Animated gradient border */}
            <div className="absolute inset-0 bg-gradient-to-r from-red-700 via-red-800 to-red-700 rounded-xl opacity-0 group-hover:opacity-20 blur transition-opacity duration-300 pointer-events-none"></div>

            {/* Animated glow effect */}
            <div className="absolute -inset-px bg-gradient-to-r from-red-700 via-red-800 to-red-700 rounded-xl opacity-0 group-hover:opacity-15 blur-lg animate-pulse transition-opacity duration-300 pointer-events-none"></div>

            <div className="relative z-10 flex items-center justify-between">
              {/* Left Section */}
              <div className="flex items-center space-x-4 flex-1">
                <div className="w-12 h-12 bg-gradient-to-br from-red-700 to-red-900 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 13c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-gray-900 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-red-800 group-hover:to-red-900 transition-all duration-300">
                    Career Passport ✨
                  </h3>
                  <p className="text-xs text-gray-700 font-medium mt-0.5">Showcase your professional excellence</p>
                </div>
              </div>

              {/* Right Section - Coming Soon with pulse */}
              <div className="text-right flex-shrink-0">
                <div className="relative inline-block">
                  <div className="absolute inset-0 bg-gradient-to-r from-red-700 to-red-800 rounded-full opacity-30 blur animate-pulse"></div>
                  <span className="relative px-3 py-1.5 bg-gradient-to-r from-red-700 to-red-800 text-white text-xs font-bold rounded-full shadow-md group-hover:shadow-lg transition-shadow duration-300">
                    🚀 Coming soon
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Completion - Below Leaderboard */}
          {profileCompletion < 100 && (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Target className="w-5 h-5 text-yellow-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Profile Completion</h3>
                </div>
                <span className="text-xl font-bold text-yellow-600">{profileCompletion}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <div
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${profileCompletion}%` }}
                ></div>
              </div>

              {/* Pending Items */}
              {profileCompletionItems.length > 0 && (
                <div className="space-y-2 mb-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Pending items:</p>
                  <div className="space-y-1.5">
                    {profileCompletionItems
                      .filter((item) => !item.completed)
                      .map((item, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <div className="flex items-center space-x-2">
                            <AlertCircle className="w-4 h-4 text-yellow-600" />
                            <span className="text-gray-700">{item.label}</span>
                          </div>
                          <span className="text-gray-500 font-medium">{item.weight}%</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-600 mb-3">
                Complete your profile to unlock more features and improve your visibility to employers.
              </p>
              <button
                onClick={() => navigate("/job-seeker/my-profile")}
                className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium"
              >
                Complete Profile →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
